import { useEffect, useState } from "react";
import type { ReactNode } from "react";

type Page = "home" | "privacy" | "terms" | "notFound";

const SUPPORT_EMAIL = "support@dosenook.com";

const pathToPage = (path: string): Page => {
  switch (path.replace(/\/+$/, "") || "/") {
    case "/":
      return "home";
    case "/privacy":
      return "privacy";
    case "/terms":
      return "terms";
    default:
      return "notFound";
  }
};

const navigateTo = (path: string): void => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

function useCurrentPage(): Page {
  const [page, setPage] = useState<Page>(() =>
    pathToPage(window.location.pathname)
  );

  useEffect(() => {
    const handleNavigation = (): void => {
      setPage(pathToPage(window.location.pathname));
      window.scrollTo({ top: 0, behavior: "instant" });
    };

    window.addEventListener("popstate", handleNavigation);
    return () => window.removeEventListener("popstate", handleNavigation);
  }, []);

  return page;
}

function PageLink({
  href,
  children,
  className
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <a
      className={className}
      href={href}
      onClick={(event) => {
        event.preventDefault();
        navigateTo(href);
      }}
    >
      {children}
    </a>
  );
}

function Header() {
  return (
    <header className="site-header">
      <PageLink className="brand" href="/">
        <img src="/dosenook-symbol.png" alt="" aria-hidden="true" />
        <span>DoseNook</span>
      </PageLink>
      <nav aria-label="Primary navigation">
        <PageLink href="/privacy">Privacy</PageLink>
        <PageLink href="/terms">Terms</PageLink>
        <a href={`mailto:${SUPPORT_EMAIL}`}>Contact</a>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div>
        <strong>DoseNook</strong>
        <p>Right dose. Right time.</p>
      </div>
      <nav aria-label="Footer navigation">
        <PageLink href="/privacy">Privacy Policy</PageLink>
        <PageLink href="/terms">Terms</PageLink>
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </nav>
    </footer>
  );
}

function HomePage() {
  return (
    <>
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="eyebrow">
            Medication support, currently in development
          </p>
          <h1 id="hero-title">Right dose. Right time.</h1>
          <p className="hero-summary">
            DoseNook is being built to help people keep medication schedules
            clear, reminders dependable, and dose history easier to understand.
          </p>
          <div className="hero-actions">
            <a className="primary-action" href={`mailto:${SUPPORT_EMAIL}`}>
              Contact DoseNook
            </a>
            <PageLink className="secondary-action" href="/privacy">
              Read privacy approach
            </PageLink>
          </div>
        </div>
        <div className="hero-logo" aria-label="DoseNook logo">
          <img src="/dosenook-icon.png" alt="DoseNook logo" />
        </div>
      </section>

      <main id="main-content">
        <section className="content-section" aria-labelledby="purpose-title">
          <div>
            <p className="section-kicker">Purpose</p>
            <h2 id="purpose-title">A simple place for medication reminders</h2>
          </div>
          <p>
            DoseNook is an assistive product in development. It is intended to
            help users record medication schedules, receive reminders, and keep
            a user-reported history of actions such as taken or skipped.
          </p>
        </section>

        <section className="feature-grid" aria-label="DoseNook priorities">
          <article>
            <h3>Clear schedules</h3>
            <p>
              Designed around plain-language medication schedule information,
              with local time and user-entered details kept visible and
              understandable.
            </p>
          </article>
          <article>
            <h3>Gentle reminders</h3>
            <p>
              Built toward reminder experiences that support everyday routines
              without making clinical decisions or replacing professional
              advice.
            </p>
          </article>
          <article>
            <h3>Useful history</h3>
            <p>
              Planned to help users see what DoseNook has recorded, while
              preserving the difference between scheduled, taken, skipped, and
              missed doses.
            </p>
          </article>
        </section>

        <section className="status-panel" aria-labelledby="status-title">
          <div>
            <p className="section-kicker">Status</p>
            <h2 id="status-title">DoseNook is currently in development</h2>
          </div>
          <p>
            We are preparing the product foundation carefully before public
            release. For questions, contact{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </section>
      </main>
    </>
  );
}

function PrivacyPage() {
  return (
    <main className="legal-page" id="main-content">
      <p className="section-kicker">Privacy Policy</p>
      <h1>Privacy Policy</h1>
      <p className="effective-date">
        Effective date: To be finalized before public launch.
      </p>
      <p>
        DoseNook is currently in development. This public website collects no
        medication information, account information, analytics events, or
        marketing sign-ups.
      </p>
      <h2>Information you choose to send</h2>
      <p>
        If you contact us by email at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>, we receive the
        information included in your message so we can respond.
      </p>
      <h2>Product privacy stance</h2>
      <p>
        DoseNook treats medication schedules and related health information as
        sensitive by default. Future product features will be designed to
        minimize data collection, protect information in transit and at rest,
        and avoid using health content for unrelated purposes.
      </p>
      <h2>Contact</h2>
      <p>
        Questions about this policy can be sent to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </main>
  );
}

function TermsPage() {
  return (
    <main className="legal-page" id="main-content">
      <p className="section-kicker">Terms</p>
      <h1>Terms</h1>
      <p className="effective-date">
        Effective date: To be finalized before public launch.
      </p>
      <p>
        DoseNook is currently in development. This website provides general
        product information only and does not provide medical, clinical, legal,
        or emergency advice.
      </p>
      <h2>Assistive purpose</h2>
      <p>
        DoseNook is intended as a medication-management aid. It is not a
        substitute for instructions from a doctor, pharmacist, nurse, or other
        qualified healthcare professional.
      </p>
      <h2>Website availability</h2>
      <p>
        We aim to keep this website available, but it may change as the product
        develops. No account portal, medication data entry, or backend
        integration is provided on this public website.
      </p>
      <h2>Contact</h2>
      <p>
        Questions about these terms can be sent to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </main>
  );
}

function NotFoundPage() {
  return (
    <main className="not-found-page" id="main-content">
      <p className="section-kicker">Page not found</p>
      <h1>We could not find that page.</h1>
      <p>
        The DoseNook public website is small right now. You can return to the
        homepage or contact us at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
      <PageLink className="primary-action" href="/">
        Return home
      </PageLink>
    </main>
  );
}

export function App() {
  const page = useCurrentPage();

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <Header />
      {page === "home" && <HomePage />}
      {page === "privacy" && <PrivacyPage />}
      {page === "terms" && <TermsPage />}
      {page === "notFound" && <NotFoundPage />}
      <Footer />
    </div>
  );
}
